import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { createApiKey } from '../../services/apiKeyService';
import { ApiKeyResponse } from '../../types/apiKey';
import { Loader2, Copy, Check, Key } from 'lucide-react';

interface CreateApiKeyDialogProps {
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

export const CreateApiKeyDialog: React.FC<CreateApiKeyDialogProps> = ({
  onSuccess,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [createdKey, setCreatedKey] = useState<ApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a name for the API key',
        variant: 'destructive',
      });
      return;
    }

    const days = parseInt(expiresInDays);
    if (isNaN(days) || days < 1 || days > 365) {
      toast({
        title: 'Validation Error',
        description: 'Expiration must be between 1 and 365 days',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + days);

      const apiKey = await createApiKey({
        name: name.trim(),
        expiresAt,
      });

      setCreatedKey(apiKey);

      toast({
        title: 'API Key Created',
        description: 'Your API key has been created successfully. Make sure to copy it now!',
      });
    } catch (error) {
      console.error('Error creating API key:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create API key',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Copied!',
        description: 'API key copied to clipboard',
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setName('');
    setExpiresInDays('30');
    setCreatedKey(null);
    setCopied(false);
    if (createdKey) {
      onSuccess();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        handleClose();
      } else {
        setOpen(true);
      }
    }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Key className="w-4 h-4 mr-2" />
            Create API Key
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {createdKey ? 'API Key Created' : 'Create New API Key'}
          </DialogTitle>
        </DialogHeader>

        {!createdKey ? (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="e.g., Production API Key"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                A descriptive name to help you identify this key
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires">Expires In (days)</Label>
              <Input
                id="expires"
                type="number"
                min="1"
                max="365"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Number of days until this key expires (1-365)
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create API Key
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium mb-2">
                ⚠️ Important: Copy your API key now
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-400">
                This is the only time you'll be able to see this key. Store it securely!
              </p>
            </div>

            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="flex items-center space-x-2">
                <Input
                  value={createdKey.key}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopy}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={createdKey.name} readOnly />
            </div>

            <div className="space-y-2">
              <Label>Expires</Label>
              <Input
                value={createdKey.expiresAt.toLocaleDateString()}
                readOnly
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
